import { MenuItemProps, Stack, Typography } from "@mui/material";
import { BorderStyle, getIconProps, ICON_SIZE } from "@pulse/shared-components";
import {
  CornerRadius,
  FoundationColorTokens,
  Spacing,
  Tabs,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { ArrowLeftRight, SquarePen, Trash2 } from "lucide-react";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useState } from "react";
import { MenuButton } from "../../../../components/buttons/MenuButton";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import {
  NavigateToFunctions,
  processSurveyParams,
} from "../../../../routes/RoutesHelper";
import { TranslationsStore } from "../../store/TranslationsStore";
import { ChangeLanguageOrientationDialog } from "./ChangeLanguageOrientationDialog";
import { DeleteSurveyLanguageDialog } from "./DeleteSurveyLanguageDialog";
import { RenameSurveyLanguageDialog } from "./RenameSurveyLanguageDialog";
import { TranslationDataList } from "./TranslationDataList";

export interface TranslationDataTabsProps {
  translationsStore: Instance<typeof TranslationsStore>;
  border: BorderStyle;
  borderRadius: CornerRadius;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  t: TFunction;
  spacing: Spacing;
  navigateTo: NavigateToFunctions;
}

interface LanguageActionContext {
  surveyId: string;
  projectId: string;
  languageId: string;
  languageCode: string;
}

interface OrientationActionContext extends LanguageActionContext {
  isRTL: boolean;
}

type SuccessAction = "rename" | "delete";

export const TranslationDataTabs = observer(
  ({
    translationsStore,
    border,
    borderRadius,
    typography,
    tokens,
    t,
    spacing,
    navigateTo,
  }: TranslationDataTabsProps): React.ReactElement => {
    const [orientationContext, setOrientationContext] =
      useState<OrientationActionContext | null>(null);
    const [renameContext, setRenameContext] =
      useState<LanguageActionContext | null>(null);
    const [deleteContext, setDeleteContext] =
      useState<LanguageActionContext | null>(null);
    const [successAction, setSuccessAction] = useState<SuccessAction | null>(
      null,
    );
    const currentLanguage = translationsStore.translatedLanguage;

    const openLanguageDialog = async (
      action: "orientation" | "rename" | "delete",
    ): Promise<void> => {
      if (currentLanguage === null) {
        return;
      }
      await processSurveyParams(async (surveyId, projectId) => {
        const context: LanguageActionContext = {
          surveyId,
          projectId,
          languageId: currentLanguage.languageId,
          languageCode: currentLanguage.languageCode,
        };
        switch (action) {
          case "orientation":
            setOrientationContext({
              ...context,
              isRTL: currentLanguage.isRTL,
            });
            break;
          case "rename":
            setRenameContext(context);
            break;
          case "delete":
            setDeleteContext(context);
            break;
        }
      }, navigateTo);
    };

    const getLanguageSettings = (): React.ReactElement => {
      if (currentLanguage === null) {
        return <></>;
      }
      const languageActionMenuItems: MenuItemProps[] = [
        {
          onClick: () => openLanguageDialog("orientation"),
          disabled: translationsStore.isLanguageActionInProgress,
          sx: {
            minWidth: "max-content",
            gap: spacing.spaceXS,
            whiteSpace: "nowrap",
            alignItems: "center",
            "&:hover": {
              backgroundColor: tokens.backgroundHovered,
            },
          },
          children: (
            <>
              <ArrowLeftRight
                {...getIconProps(tokens.icon, ICON_SIZE.default)}
              />
              <Typography {...typography.b2} color={tokens.label} noWrap={true}>
                {t(
                  "surveys.translations.languageSettings.changeOrientationAction",
                )}
              </Typography>
            </>
          ),
        },
        {
          onClick: () => openLanguageDialog("rename"),
          disabled: translationsStore.isLanguageActionInProgress,
          sx: {
            minWidth: "max-content",
            gap: spacing.spaceXS,
            whiteSpace: "nowrap",
            alignItems: "center",
            "&:hover": {
              backgroundColor: tokens.backgroundHovered,
            },
          },
          children: (
            <>
              <SquarePen {...getIconProps(tokens.icon, ICON_SIZE.default)} />
              <Typography {...typography.b2} color={tokens.label} noWrap={true}>
                {t("surveys.translations.languageSettings.renameAction")}
              </Typography>
            </>
          ),
        },
        {
          onClick: () => openLanguageDialog("delete"),
          disabled: translationsStore.isLanguageActionInProgress,
          sx: {
            minWidth: "max-content",
            gap: spacing.spaceXS,
            whiteSpace: "nowrap",
            alignItems: "center",
            "&:hover": {
              backgroundColor: tokens.backgroundErrorSubtle,
            },
          },
          children: (
            <>
              <Trash2 {...getIconProps(tokens.iconError, ICON_SIZE.default)} />
              <Typography
                {...typography.b2}
                color={tokens.labelError}
                noWrap={true}
              >
                {t("surveys.translations.languageSettings.deleteAction")}
              </Typography>
            </>
          ),
        },
      ];

      return (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
        >
          <MenuButton
            button={{
              label: t("surveys.translations.languageSettings.actionLabel"),
              name: "languageActions",
              size: "medium",
              variant: "outlined-neutral",
              disabled: translationsStore.isLanguageActionInProgress,
            }}
            menu={{
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              transformOrigin: { vertical: "top", horizontal: "right" },
            }}
            menuItems={languageActionMenuItems}
          />
        </Stack>
      );
    };

    const TranslationTabOrList = observer((): React.ReactElement => {
      if (translationsStore.isSingleLanguagePresent) {
        return (
          <Stack gap={spacing.spaceXL}>
            {getLanguageSettings()}
            <TranslationDataList
              translationsStore={translationsStore}
              border={border}
              borderRadius={borderRadius}
              typography={typography}
              tokens={tokens}
              t={t}
              spacing={spacing}
            />
          </Stack>
        );
      }
      return (
        <Stack gap={spacing.spaceXL}>
          <Tabs
            tabs={translationsStore.translationListTabs}
            onTabChange={(index: number) => {
              translationsStore.setSelectedLanguageBasedOnIndex(index);
            }}
            selectedTabIndex={translationsStore.selectedLanguage?.tabIndex ?? 0}
          />
          {getLanguageSettings()}
          <TranslationDataList
            translationsStore={translationsStore}
            border={border}
            borderRadius={borderRadius}
            typography={typography}
            tokens={tokens}
            t={t}
            spacing={spacing}
          />
        </Stack>
      );
    });

    return (
      <>
        <Stack margin={`${spacing.spaceXL} 0px`} gap={spacing.spaceLG}>
          <TranslationTabOrList />
        </Stack>
        {orientationContext !== null && (
          <ChangeLanguageOrientationDialog
            open={true}
            {...orientationContext}
            translationsStore={translationsStore}
            t={t}
            spacing={spacing}
            typography={typography}
            tokens={tokens}
            onClose={() => setOrientationContext(null)}
            onSuccess={async () => {
              await translationsStore.getAllTranslations(
                orientationContext.surveyId,
                orientationContext.projectId,
              );
            }}
          />
        )}
        {renameContext !== null && (
          <RenameSurveyLanguageDialog
            open={true}
            {...renameContext}
            translationsStore={translationsStore}
            t={t}
            spacing={spacing}
            typography={typography}
            tokens={tokens}
            onClose={() => setRenameContext(null)}
            onSuccess={async () => {
              await translationsStore.getAllTranslations(
                renameContext.surveyId,
                renameContext.projectId,
              );
              setSuccessAction("rename");
            }}
          />
        )}
        {deleteContext !== null && (
          <DeleteSurveyLanguageDialog
            open={true}
            {...deleteContext}
            translationsStore={translationsStore}
            t={t}
            spacing={spacing}
            typography={typography}
            tokens={tokens}
            onClose={() => setDeleteContext(null)}
            onSuccess={async () => {
              translationsStore.selectFirstTranslation();
              await translationsStore.getAllTranslations(
                deleteContext.surveyId,
                deleteContext.projectId,
              );
              setSuccessAction("delete");
            }}
          />
        )}
        {successAction !== null && (
          <SuccessDialog
            title={t("surveys.translations.languageAction.successTitle")}
            message={
              <Stack gap={spacing.spaceSM}>
                <Typography>
                  {t(
                    successAction === "rename"
                      ? "surveys.translations.languageAction.renameSuccess"
                      : "surveys.translations.languageAction.deleteSuccess",
                  )}
                </Typography>
                <Typography>
                  {t(
                    "surveys.translations.languageAction.freshTemplateWarning",
                  )}
                </Typography>
              </Stack>
            }
            onClickClose={() => setSuccessAction(null)}
          />
        )}
      </>
    );
  },
);
